import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Users, Wifi, Dumbbell, Book, Home, Shield, UtensilsCrossed, Shirt, Car, Wind, Flame, Armchair, PawPrint } from "lucide-react";
import { Layout } from "@/frontend/components/layout/Layout";
import { Button } from "@/frontend/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/frontend/components/ui/breadcrumb";
import { useResidence } from "@/backend/hooks/useResidence";
import { ResidenceCardSkeleton } from "@/frontend/components/residences/ResidenceCardSkeleton";
import { ResidenceMap } from "@/frontend/components/residences/ResidenceMap";
import { PolicyLinks } from "@/frontend/components/residences/PolicyLinks";
import { CommonAreasSection } from "@/frontend/components/residences/CommonAreasSection";
import { calculateMinimumStay, getMinRoomTypePrice } from "@/backend/lib/residenceUtils";

const amenityIcons: { [key: string]: React.ElementType } = {
  "High-Speed WiFi": Wifi,
  "Kitchen": UtensilsCrossed,
  "Laundry": Shirt,
  "Study Room": Book,
  "Gym": Dumbbell,
  "Parking": Car,
  "Air Conditioning": Wind,
  "Heating": Flame,
  "Furnished": Armchair,
  "24/7 Security": Shield,
  "Pet Friendly": PawPrint,
};

const ResidenceDetail = () => {
  const { id, residenceId } = useParams<{ id?: string; residenceId?: string }>();
  // Support both :id and :residenceId for backward compatibility
  const residenceIdParam = id || residenceId;
  const { data, isLoading, error } = useResidence(residenceIdParam);

  if (isLoading) {
    return (
      <Layout>
        <div className="pt-24 md:pt-28 min-h-screen">
          <div className="container-narrow section-padding">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[...Array(3)].map((_, i) => (
                <ResidenceCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="pt-24 md:pt-28 min-h-screen">
          <div className="container-narrow section-padding text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground">
              Residence not found
            </h1>
            <p className="mt-4 text-muted-foreground">
              The residence you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild className="mt-6">
              <Link to="/residences">Back to Residences</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const { residence, roomTypes } = data;
  const minStay = calculateMinimumStay(roomTypes);

  // Collapse units that share a typology into one public listing. Standalone
  // rooms (no typologyKey) keep their own card. Representative unit is the
  // oldest one (ties broken by id) — its route is what "View details" opens.
  const typologyGroupMap = new Map<string, typeof roomTypes>();
  for (const room of roomTypes) {
    const key = room.typologyKey?.trim() || room.id;
    const existing = typologyGroupMap.get(key);
    if (existing) existing.push(room);
    else typologyGroupMap.set(key, [room]);
  }
  const publicRoomTypes = Array.from(typologyGroupMap.values()).map((units) => {
    units.sort((a, b) => {
      const am = a.createdAt?.toMillis() ?? 0;
      const bm = b.createdAt?.toMillis() ?? 0;
      if (am !== bm) return am - bm;
      return a.id.localeCompare(b.id);
    });
    return units[0];
  });

  return (
    <Layout>
      <div className="pt-24 md:pt-28">
        {/* Hero Image */}
        <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
          <img
            src={residence.imageUrl}
            alt={residence.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>

        <div className="container-narrow section-padding">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-8 -mt-8 relative z-10">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/residences">Residences</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{residence.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left Column - Content */}
            <div className="lg:col-span-2 space-y-10">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                  {residence.name}
                </h1>
                <div className="flex items-center gap-2 mt-3 text-muted-foreground">
                  <MapPin className="w-5 h-5 text-secondary" />
                  <span>{residence.address}</span>
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  About This Residence
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {residence.fullDescription}
                </p>
              </div>

              {/* Amenities */}
              <div>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Amenities
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {residence.amenities.map((amenity) => {
                    const Icon = amenityIcons[amenity] || Home;
                    return (
                      <div
                        key={amenity}
                        className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                      >
                        <Icon className="w-5 h-5 text-secondary flex-shrink-0" />
                        <span className="text-sm text-foreground">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Residence Common Areas */}
              <CommonAreasSection commonAreas={residence.commonAreas} />

              {/* Rooms */}
              <div id="rooms">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                  Available Rooms
                </h2>
                <div className="space-y-4">
                  {publicRoomTypes.map((room) => {
                    const thumbnailUrl = room.imagesUrl?.[0] ?? residence.imageUrl ?? "";
                    return (
                    <Link
                      key={room.id}
                      to={`/residences/${residence.id}/rooms/${room.id}`}
                      className="block"
                    >
                      <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group">
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="md:w-56 md:flex-shrink-0 aspect-[4/3] overflow-hidden bg-muted">
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt={room.name}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                <span className="text-sm">No image</span>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 flex-1 p-5 md:py-4 md:pl-0">
                            <div className="flex-1">
                              <h3 className="text-lg font-heading font-semibold text-foreground group-hover:text-secondary transition-colors">
                                {room.name}
                              </h3>
                              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                                {room.description}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  <span>Max {room.maxOccupancy} {room.maxOccupancy === 1 ? 'person' : 'people'}</span>
                                </div>
                                <span>•</span>
                                <span>{room.area} m²</span>
                              </div>
                            </div>
                            <div className="text-right md:pr-5">
                              <p className="text-2xl font-heading font-bold text-foreground">
                                {(room.priceByOccupancy?.length ?? 0) > 1 ? "from " : ""}
                                €{getMinRoomTypePrice(room)}
                                <span className="text-sm font-normal text-muted-foreground">
                                  /month
                                </span>
                              </p>
                              <p className="text-xs text-muted-foreground mt-1 group-hover:text-secondary transition-colors">
                                View details →
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                    );
                  })}
                </div>
              </div>

              {/* Location */}
              <div>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Location
                </h2>
                <p className="text-muted-foreground mb-4">{residence.address}</p>
                <ResidenceMap address={residence.address} residenceName={residence.name} />
              </div>
            </div>

            {/* Right Column - Info Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-card border border-border rounded-xl p-6 shadow-sm">
                <div className="mb-6">
                  <span className="text-sm text-muted-foreground">From</span>
                  <p className="text-3xl font-heading font-bold text-foreground">
                    {residence.startingPrice !== null && residence.startingPrice !== undefined ? (
                      <>
                        €{residence.startingPrice}
                        <span className="text-base font-normal text-muted-foreground">
                          /month
                        </span>
                      </>
                    ) : (
                      <span className="text-base font-normal text-muted-foreground">
                        Price on request
                      </span>
                    )}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Minimum stay: {minStay} month{minStay > 1 ? 's' : ''}
                </p>
                {roomTypes.length > 0 && (
                  <Button
                    variant="coral"
                    size="lg"
                    className="w-full"
                    onClick={() => {
                      // Scroll to rooms section with offset for header
                      const roomTypesSection = document.getElementById('rooms');
                      if (roomTypesSection) {
                        const headerOffset = 120;
                        const elementPosition = roomTypesSection.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth'
                        });
                      }
                    }}
                  >
                    View Rooms to Book
                  </Button>
                )}
                <PolicyLinks residenceId={residence.id} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResidenceDetail;
